import React, { useState, useMemo, useRef } from 'react';
// FIX: Removed 'MapPin' from the import list as it was defined but never used.
import { Search, UtensilsCrossed, Star } from 'lucide-react';
import RestaurantListItem from './RestaurantListItem';
import RestaurantDetail from './RestaurantDetail';
import heroImage from '../assets/hero.png';
import mainLogo from '../assets/mainlogo.png';

/**
 * Calculates the distance between two geographical points.
 * @param {number} lat1 - Latitude of the first point.
 * @param {number} lon1 - Longitude of the first point.
 * @param {number} lat2 - Latitude of the second point.
 * @param {number} lon2 - Longitude of the second point.
 * @returns {number | null} The distance in kilometers, or null if location is not available.
 */
const getDistance = (lat1, lon1, lat2, lon2) => {
    if (lat1 === null || lon1 === null) return null;
    const R = 6371; // Radius of the Earth in km
    const dLat = (lat2 - lat1) * (Math.PI / 180);
    const dLon = (lon2 - lon1) * (Math.PI / 180);
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
};

/**
 * Checks if a string is a valid UK postcode.
 * @param {string} str - The string to test.
 * @returns {boolean} True if the string is a postcode.
 */
const isPostcode = (str) => /^[A-Z]{1,2}[0-9]{1,2}[A-Z]?\s?[0-9][A-Z]{2}$/i.test(str.trim());

export default function HomePage({ restaurants, user, favorites, toggleFavorite }) {
    const [searchTerm, setSearchTerm] = useState('');
    const [userLocation, setUserLocation] = useState(null);
    const [locationError, setLocationError] = useState('');
    const [isLocating, setIsLocating] = useState(false);
    const [filters, setFilters] = useState({ cuisine: [], price: 'all', rating: 0 });
    const [sortBy, setSortBy] = useState('distance');
    const [selectedRestaurant, setSelectedRestaurant] = useState(null);

    const mainContentRef = useRef(null);

    // Handles the search form submission.
    const handleSearchSubmit = async (e) => {
        e.preventDefault();
        if (!searchTerm.trim()) return;

        // If the search term is a postcode, geocode it to get coordinates.
        if (isPostcode(searchTerm)) {
            setIsLocating(true);
            setLocationError('');
            try {
                const response = await fetch('/api/geocode', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ postcode: searchTerm }),
                });
                if (!response.ok) throw new Error('Invalid postcode.');
                const { latitude, longitude } = await response.json();
                setUserLocation({ latitude, longitude });
                mainContentRef.current?.scrollIntoView({ behavior: 'smooth' });
            } catch (err) {
                setLocationError(err.message);
            } finally {
                setIsLocating(false);
            }
        } else {
            // Otherwise, just scroll to the results.
            mainContentRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    };

    // Toggles a cuisine in the filter state.
    const handleCuisineChange = (cuisine) => {
        setFilters(prev => ({
            ...prev,
            cuisine: prev.cuisine.includes(cuisine)
                ? prev.cuisine.filter(c => c !== cuisine)
                : [...prev.cuisine, cuisine]
        }));
    };
    
    // Handlers for selecting a restaurant and going back to the list.
    const handleSelectRestaurant = (restaurant) => setSelectedRestaurant(restaurant);
    const handleBackToList = () => setSelectedRestaurant(null);

    // Memoized calculation to filter and sort restaurants whenever dependencies change.
    const filteredAndSortedRestaurants = useMemo(() => {
        let processed = restaurants.map(r => ({ ...r, distance: userLocation ? getDistance(userLocation.latitude, userLocation.longitude, r.latitude, r.longitude) : null }));
        
        // Apply filters
        if (filters.cuisine.length > 0) processed = processed.filter(r => filters.cuisine.includes(r.cuisine));
        if (filters.price !== 'all') processed = processed.filter(r => r.priceRange === filters.price);
        if (filters.rating > 0) processed = processed.filter(r => r.rating >= filters.rating);
        
        // Apply search term filter (if it's not a postcode)
        const currentSearchTerm = searchTerm.trim();
        if (currentSearchTerm && !isPostcode(currentSearchTerm)) {
            const lowercasedFilter = currentSearchTerm.toLowerCase();
            processed = processed.filter(r => r.name.toLowerCase().includes(lowercasedFilter) || r.cuisine.toLowerCase().includes(lowercasedFilter));
        }

        // Apply sorting
        switch(sortBy) {
            case 'distance': processed.sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity)); break;
            case 'rating': processed.sort((a, b) => b.rating - a.rating); break;
            case 'name': processed.sort((a, b) => a.name.localeCompare(b.name)); break;
            default: break;
        }
        return processed;
    }, [searchTerm, userLocation, restaurants, filters, sortBy]);
    
    // Get a sorted list of unique cuisines for the filter panel.
    const uniqueCuisines = [...new Set(restaurants.map(r => r.cuisine))].sort();

    return (
        <div>
            {/* Hero Section */}
            <div className="h-screen flex items-center justify-center text-center px-4 sm:px-6 lg:px-8 bg-cover bg-center bg-fixed" style={{backgroundImage: `url(${heroImage})`}}>
                <div className="absolute inset-0 bg-gray-900 opacity-60"></div>
                <div className="relative max-w-4xl mx-auto">
                    <img src={mainLogo} alt="GoEatOut Logo" className="mx-auto h-32 sm:h-40 mb-8" />
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight">Find & Book Your Next Meal</h1>
                    <p className="mt-4 text-xl text-gray-300">Discover top-rated local restaurants and exclusive vouchers.</p>
                    <form onSubmit={handleSearchSubmit} className="mt-10 max-w-xl mx-auto">
                        <div className="relative">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 z-10" />
                            <input 
                                type="text" 
                                placeholder="Enter restaurant, cuisine, or postcode..." 
                                value={searchTerm} 
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-12 p-4 border-transparent rounded-lg shadow-lg text-lg" 
                            />
                        </div>
                        {locationError && <p className="text-red-300 text-sm mt-3">{locationError}</p>}
                    </form>
                </div>
            </div>

            {/* Main Content Area */}
            <div ref={mainContentRef} className="relative bg-gray-100 p-4 sm:p-8">
                <div className="container mx-auto grid grid-cols-1 lg:grid-cols-4 gap-8">
                    {/* Filters Sidebar */}
                    <aside className="lg:col-span-1 bg-white p-6 rounded-xl shadow-lg h-fit sticky top-24">
                        <h3 className="text-xl font-bold mb-4">Filters</h3>
                        <div className="space-y-6">
                            {/* Cuisine Filter */}
                            <div>
                                <h4 className="font-semibold mb-2">Cuisine</h4>
                                <div className="space-y-2 max-h-60 overflow-y-auto">
                                    {uniqueCuisines.map(cuisine => (
                                        <label key={cuisine} className="flex items-center">
                                            <input type="checkbox" checked={filters.cuisine.includes(cuisine)} onChange={() => handleCuisineChange(cuisine)} className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                                            <span className="ml-3 text-gray-700">{cuisine}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                            {/* Price Filter */}
                            <div>
                                <h4 className="font-semibold mb-2">Price Range</h4>
                                <div className="flex justify-around bg-gray-200 rounded-lg p-1">
                                    {['all', '£', '££', '£££'].map(price => (
                                        <button key={price} onClick={() => setFilters(p => ({...p, price}))} className={`w-full py-1 rounded-md text-sm font-bold ${filters.price === price ? 'bg-white shadow' : 'bg-transparent text-gray-600'}`}>
                                            {price === 'all' ? 'All' : price}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {/* Rating Filter */}
                            <div>
                                <h4 className="font-semibold mb-2">Rating</h4>
                                <div className="flex items-center justify-center gap-1">
                                    {[...Array(5)].map((_, i) => (
                                        <Star key={i} onClick={() => setFilters(p => ({...p, rating: i + 1}))} className={`w-8 h-8 cursor-pointer ${i < filters.rating ? 'text-yellow-400' : 'text-gray-300 hover:text-yellow-300'}`} fill="currentColor" />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </aside>

                    {/* Restaurant List / Detail View */}
                    <main className="lg:col-span-3">
                         <div className="bg-white p-4 rounded-xl shadow-lg mb-6 sticky top-24 z-30 flex flex-col sm:flex-row gap-4 items-center">
                            <div className="relative flex-grow w-full">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input type="text" placeholder="Search or enter postcode..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 p-3 border border-gray-300 rounded-lg" />
                            </div>
                            <button onClick={handleSearchSubmit} disabled={isLocating} className="p-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 w-full sm:w-auto">
                                {isLocating ? 'Searching...' : 'Search'}
                            </button>
                            <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="w-full sm:w-auto p-3 border border-gray-300 rounded-lg">
                                <option value="distance">Sort by Distance</option>
                                <option value="rating">Sort by Rating</option>
                                <option value="name">Sort by Name (A-Z)</option>
                            </select>
                         </div>
                        
                        {selectedRestaurant ? (
                            <RestaurantDetail restaurant={selectedRestaurant} user={user} onBack={handleBackToList} />
                        ) : (
                             <div className="space-y-6">
                                {filteredAndSortedRestaurants.length > 0 ? (
                                    filteredAndSortedRestaurants.map((restaurant) => (
                                        <RestaurantListItem key={restaurant.id} restaurant={restaurant} isFavorite={favorites.includes(restaurant.id)} toggleFavorite={toggleFavorite} onSelect={handleSelectRestaurant} />
                                    ))
                                ) : (
                                    <div className="text-center py-16 px-4 bg-white rounded-lg shadow-md">
                                        <UtensilsCrossed className="mx-auto h-16 w-16 text-gray-400" />
                                        <h2 className="mt-4 text-2xl font-semibold text-gray-800">No Restaurants Found</h2>
                                        <p className="text-gray-500">Try adjusting your search or filters.</p>
                                    </div>
                                )}
                             </div>
                        )}
                    </main>
                </div>
            </div>
        </div>
    );
}
